import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { CheckCircle, Warning, DownloadSimple, Sparkle } from "@phosphor-icons/react";

export default function ReviewDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    const { data: j } = await api.get(`/jobs/${jobId}`);
    setJob(j);
    if (j.status === "in_review") {
      try {
        const { data } = await api.get(`/jobs/${jobId}/checklist`);
        setChecklist(data.checklist);
      } catch (e) { /* ignore */ }
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (idx) => setChecklist(checklist.map((c, i) => i === idx ? { ...c, checked: !c.checked } : c));

  const submitDecision = async (decision) => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/review`, { checklist, decision, comment });
      if (decision === "approved" && data.payout) {
        toast.success(`Approved — appraiser: $${data.payout.appraiser.toFixed(2)} · reviewer: $${data.payout.reviewer.toFixed(2)} · platform: $${data.payout.platform.toFixed(2)}`);
      } else {
        toast.success(decision === "approved" ? "Approved" : "Revision requested");
      }
      navigate("/review-pool", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  if (!job) return <PageLayout><div className="text-zinc-500">Loading…</div></PageLayout>;

  return (
    <PageLayout className="pb-32 lg:pb-12">
      <div className="max-w-4xl">
        <div className="kicker">Peer review</div>
        <h1 className="display-serif text-4xl sm:text-5xl mt-2">{job.address}</h1>
        <div className="text-zinc-600 mt-1">{job.city}, {job.state} {job.zip_code}</div>

        {job.report_path && (
          <a
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-white px-4 py-2 text-sm hover:bg-zinc-50"
            href={`${api.defaults.baseURL}/files/${job.report_path}`}
            target="_blank"
            rel="noreferrer"
            data-testid="review-report-download"
          >
            <DownloadSimple size={16} /> Open report ({job.report_path.split("/").pop()})
          </a>
        )}

        <section className="mt-8 surface-card p-6 lg:p-8">
          <div className="flex items-center gap-2"><Sparkle size={18} className="text-[#9A3412]" weight="fill" /><h3 className="display-serif text-2xl">AI QA checklist</h3></div>
          <p className="text-sm text-zinc-600 mt-1">Verify each item, add notes if needed.</p>
          {!checklist ? <div className="mt-4 text-zinc-500">Generating checklist…</div> : (
            <ul className="mt-5 space-y-3">
              {checklist.map((c, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`review-item-${i}`}>
                  <Checkbox checked={c.checked} onCheckedChange={() => toggle(i)} className="mt-1" />
                  <div className="flex-1">
                    <div className="text-sm">{c.question}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-6 surface-card p-6 lg:p-8">
          <h3 className="display-serif text-xl">Reviewer notes</h3>
          <Textarea rows={4} value={comment} onChange={e => setComment(e.target.value)} placeholder="Optional — what to fix, or why you're approving" data-testid="review-comment" />
        </section>
      </div>

      {/* sticky bottom bar (mobile-friendly thumb access) */}
      <div className="fixed bottom-0 left-0 right-0 z-40 backdrop-blur-lg bg-white/90 border-t border-zinc-200 lg:relative lg:mt-6 lg:bg-transparent lg:border-0 lg:backdrop-blur-none">
        <div className="max-w-4xl mx-auto px-5 py-4 flex gap-3">
          <button
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-zinc-300 bg-white text-zinc-900 px-4 py-3 text-sm font-medium hover:bg-zinc-50"
            disabled={submitting}
            onClick={() => submitDecision("revision_requested")}
            data-testid="review-request-revision-btn"
          >
            <Warning size={16} /> Request revision
          </button>
          <button
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#115E59] text-white px-4 py-3 text-sm font-medium hover:bg-[#042F2E]"
            disabled={submitting}
            onClick={() => submitDecision("approved")}
            data-testid="review-approve-btn"
          >
            <CheckCircle size={16} weight="fill" /> {submitting ? "Submitting…" : "Approve & release funds"}
          </button>
        </div>
      </div>
    </PageLayout>
  );
}
